import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isBetween from 'dayjs/plugin/isBetween';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Extend dayjs with plugins
dayjs.extend(weekOfYear);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

// Export dayjs as default
export default dayjs;

// Re-export commonly used functions with dayjs equivalents
export const format = (date: Date | string | dayjs.Dayjs, formatStr: string) => dayjs(date).format(formatStr);

export const addDays = (date: Date | string | dayjs.Dayjs, amount: number) => dayjs(date).add(amount, 'day').toDate();

export const addWeeks = (date: Date | string | dayjs.Dayjs, amount: number) => dayjs(date).add(amount, 'week').toDate();

export const addMonths = (date: Date | string | dayjs.Dayjs, amount: number) =>
  dayjs(date).add(amount, 'month').toDate();

export const addYears = (date: Date | string | dayjs.Dayjs, amount: number) => dayjs(date).add(amount, 'year').toDate();

export const subDays = (date: Date | string | dayjs.Dayjs, amount: number) =>
  dayjs(date).subtract(amount, 'day').toDate();

export const subWeeks = (date: Date | string | dayjs.Dayjs, amount: number) =>
  dayjs(date).subtract(amount, 'week').toDate();

export const subMonths = (date: Date | string | dayjs.Dayjs, amount: number) =>
  dayjs(date).subtract(amount, 'month').toDate();

export const subYears = (date: Date | string | dayjs.Dayjs, amount: number) =>
  dayjs(date).subtract(amount, 'year').toDate();

export const isSameDay = (dateLeft: Date | string | dayjs.Dayjs, dateRight: Date | string | dayjs.Dayjs) =>
  dayjs(dateLeft).isSame(dayjs(dateRight), 'day');

export const isSameMonth = (dateLeft: Date | string | dayjs.Dayjs, dateRight: Date | string | dayjs.Dayjs) =>
  dayjs(dateLeft).isSame(dayjs(dateRight), 'month');

export const isSameYear = (dateLeft: Date | string | dayjs.Dayjs, dateRight: Date | string | dayjs.Dayjs) =>
  dayjs(dateLeft).isSame(dayjs(dateRight), 'year');

export const isToday = (date: Date | string | dayjs.Dayjs) => dayjs(date).isSame(dayjs(), 'day');

/**
 * Normalizes Mantine calendar date values (YYYY-MM-DD strings or UTC-midnight Date objects)
 * to a local calendar date. Avoid `new Date('YYYY-MM-DD')` — it parses as UTC and shifts the day.
 */
export const parseCalendarDate = (value: Date | string | dayjs.Dayjs): dayjs.Dayjs => {
  const date = dayjs(value);

  if (date.hour() !== 0) {
    return dayjs(date.utc().format('YYYY-MM-DD'));
  }

  return date;
};

export const getLocalDayKey = (value: Date | string | dayjs.Dayjs): number => {
  const dateStr = parseCalendarDate(value).format('YYYY-MM-DD');
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).getTime();
};

export const startOfWeek = (date: Date | string | dayjs.Dayjs, options?: { weekStartsOn?: number }) => {
  const weekStartsOn = options?.weekStartsOn ?? 0; // 0 = Sunday, 1 = Monday
  const d = dayjs(date);
  const day = d.day();
  const diff = day - weekStartsOn;
  return d.subtract(diff, 'day').startOf('day').toDate();
};

export const endOfWeek = (date: Date | string | dayjs.Dayjs, options?: { weekStartsOn?: number }) => {
  const weekStartsOn = options?.weekStartsOn ?? 0; // 0 = Sunday, 1 = Monday
  const d = dayjs(date);
  const day = d.day();
  const diff = (6 - day + weekStartsOn) % 7;
  return d.add(diff, 'day').endOf('day').toDate();
};

export const startOfMonth = (date: Date | string | dayjs.Dayjs) => dayjs(date).startOf('month').toDate();

export const endOfMonth = (date: Date | string | dayjs.Dayjs) => dayjs(date).endOf('month').toDate();

export const startOfYear = (date: Date | string | dayjs.Dayjs) => dayjs(date).startOf('year').toDate();

export const endOfYear = (date: Date | string | dayjs.Dayjs) => dayjs(date).endOf('year').toDate();

export const eachDayOfInterval = (interval: {
  start: Date | string | dayjs.Dayjs;
  end: Date | string | dayjs.Dayjs;
}) => {
  const start = dayjs(interval.start);
  const end = dayjs(interval.end);
  const days: Date[] = [];

  let current = start;
  while (current.isSameOrBefore(end, 'day')) {
    days.push(current.toDate());
    current = current.add(1, 'day');
  }

  return days;
};

export const eachWeekOfInterval = (
  interval: {
    start: Date | string | dayjs.Dayjs;
    end: Date | string | dayjs.Dayjs;
  },
  _options?: { weekStartsOn?: number }
) => {
  const start = dayjs(interval.start);
  const end = dayjs(interval.end);
  const weeks: Date[] = [];

  let current = start.startOf('week');
  while (current.isSameOrBefore(end, 'week')) {
    weeks.push(current.toDate());
    current = current.add(1, 'week');
  }

  return weeks;
};

export const eachMonthOfInterval = (interval: {
  start: Date | string | dayjs.Dayjs;
  end: Date | string | dayjs.Dayjs;
}) => {
  const start = dayjs(interval.start);
  const end = dayjs(interval.end);
  const months: Date[] = [];

  let current = start.startOf('month');
  while (current.isSameOrBefore(end, 'month')) {
    months.push(current.toDate());
    current = current.add(1, 'month');
  }

  return months;
};

// date: "YYYY-MM-DD" or Date or ISO timestamp string
// time: "HH:mm" or Date or null (for all-day events)
export const toLocalDateWithDayjs = ({
  date,
  time,
}: {
  date: string | Date | null;
  time: string | Date | null;
}): Date | null => {
  if (date === null) {
    return null;
  }

  // If date is already a timestamp (ISO string), return it directly
  if (typeof date === 'string') {
    // Check if it's an ISO timestamp (contains 'T' or is a full timestamp)
    if (date.includes('T') || date.includes(' ')) {
      // If it's already a timestamp, don't combine with time
      const parsed = dayjs(date);
      if (parsed.isValid()) {
        return parsed.toDate();
      }
    }
  }

  // Extract just the date part (YYYY-MM-DD)
  const dateString = typeof date === 'string' ? date : dayjs(date).format('YYYY-MM-DD');

  // If date string contains time info, extract just the date part
  const dateOnly = dateString.split('T')[0].split(' ')[0];

  // For all-day events, time is null, so we only use the date
  if (time === null) {
    return dayjs(dateOnly, 'YYYY-MM-DD').toDate();
  }

  const timeString = typeof time === 'string' ? time : dayjs(time).format('HH:mm');
  // Extract just the time part if it's a full timestamp
  const timeOnly = timeString.split(' ')[0].split('T')[1] || timeString;
  return dayjs(`${dateOnly} ${timeOnly}`, 'YYYY-MM-DD HH:mm').toDate();
};

/**
 * Combines a date and time string into a Date object interpreted in the specified timezone
 */
const combineDateAndTime = ({
  date,
  time,
  timezone,
}: {
  date: Date | null;
  time: string;
  timezone: string | undefined;
}): Date | null => {
  if (!date || Number.isNaN(date.getTime())) {
    return null;
  }

  const resolvedTimezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateString = `${year}-${month}-${day}`;

  try {
    const timeMatch = time.trim().match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);

    if (!timeMatch || !time.trim()) {
      return dayjs.tz(dateString, 'YYYY-MM-DD', resolvedTimezone).startOf('day').toDate();
    }

    let hours = Number(timeMatch[1]);
    const minutes = Number(timeMatch[2]) || 0;
    const meridiem = timeMatch[3]?.toLowerCase();

    if (meridiem === 'pm' && hours !== 12) {
      hours += 12;
    } else if (meridiem === 'am' && hours === 12) {
      hours = 0;
    }

    const timeString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

    return dayjs.tz(`${dateString} ${timeString}`, 'YYYY-MM-DD HH:mm', resolvedTimezone).toDate();
  } catch {
    return null;
  }
};

/**
 * Combines date and time, then converts from the specified timezone to UTC
 * This is a convenience function that combines combineDateAndTime and convertToUTCTimezone
 */
export const convertDateAndTimeToUTC = ({
  date,
  time,
  timezone,
}: {
  date: Date | null;
  time: string;
  timezone: string | undefined;
}): string | null => {
  const combinedDate = combineDateAndTime({ date, time, timezone });
  if (!combinedDate) {
    return null;
  }
  return dayjs(combinedDate).toISOString();
};

/**
 * Parses an ISO date string into date and time in the specified timezone.
 * Used to prefill date/time pickers from announcement startDate/endDate (UTC ISO).
 * Returns time in HH:mm (24h) so Mantine TimePicker value is valid.
 */
export const parseISOToDateAndTimeInTimezone = (
  isoString: string | null | undefined,
  timezone: string | undefined
): { date: Date | null; time: string } => {
  if (!isoString) {
    return { date: null, time: '' };
  }
  const resolvedTimezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const d = dayjs.utc(isoString).tz(resolvedTimezone);
  if (!d.isValid()) {
    return { date: null, time: '' };
  }
  return {
    date: d.toDate(),
    time: d.format('HH:mm'),
  };
};
